// КОРПУС КОПИ ВИДА `intro` (ШАГ 3) — восемь слотов `s3.intro.*`. B5-2.
//
// ИСТОЧНИК — §9 спеки шага 3 («Экран `intro` — вида не было в спеке вообще»), она же дословно
// снята с байта борда `Life Map - s3 Deck v8.html`, функция `intro()` (`:461–474`). Сверка
// машинная — `src/render/__parity__/wizard-s3-copy.test.js`.
//
// ⚠️ СЛОТОВ В СПЕКЕ ДЕВЯТЬ, А ЗДЕСЬ ВОСЕМЬ, И ЭТО НЕ НЕДОСЧЁТ. Девятый — `s3.intro.counter`
// (`step 03 / 07`), и его печатает ОБОЛОЧКА на всех семи шагах (`WizardShell.jsx` + `stepCounter`
// в `address.js`). Завести его копию здесь значило бы завести вторую правду о том, где стоит
// счётчик, — ровно то, чего экран шага 1 не делает по той же причине. Тикет B5-2 просит восемь
// строк; расхождение с таблицей §9 объясняется этим и только этим.
//
// ⚠️ ДВЕ ИЗ ВОСЬМИ СТРОК ПЕЧАТАЕТ НЕ ТЕЛО ЭКРАНА, А ФУТЕР ОБОЛОЧКИ — `s3.intro.cta` и
// `s3.intro.back`. Авторство при этом остаётся здесь: у борда они лежат в его собственном
// `foot-row`, а у нас ряд футера один на весь визард, и строку в него приносит шаг (тот же
// механизм, которым шаг 2 приносит надпись гейта). Дом строки и место её печати — разные вопросы.
//
// ⛔ ЧИСЛА `{N}` БЕРУТСЯ ИЗ КОНВЕРТА, А НЕ ИЗ БОРДА. У борда `N` — дев-переключатель (`let N=5`,
// `:413`), у нас это `wizard.stages.length`. Поэтому строки с числом — функции, а не литералы:
// литерал с зашитой пятёркой прошёл бы парити на байте борда и врал бы на экране.

import { roman } from '../../roman.js';
import { stepCounter } from '../address.js';
import { stageRow } from './stageList.js';

/** Надзаголовок вида (`s3.intro.head`, борд `:463`). Он же якорь тоста шага 2 (`s855.d1`). */
export const INTRO_HEAD = 'the second pass';

/**
 * Лид в ДВЕ строки (`s3.intro.lede`, борд `:464`), а не одна с переносом.
 *
 * Слово `inside` — янтарное (борд: `<span class="amb">`, у нас `.s3-amb`), поэтому вторая строка
 * разложена на три куска:
 * склеенная строка потеряла бы выделение, как это уже случилось бы со счётчиком гейта у шага 2.
 */
export const INTRO_LEDE = {
  first: 'You named the stages.',
  secondLead: 'Now we go ',
  secondAccent: 'inside',
  secondTrail: ' them.',
};

/** Подпись списка этапов (`s3.intro.count`, борд `:466`). Число идёт отдельным узлом. */
export const INTRO_COUNT_LABEL = 'your stages';

/** Подпись под линией-спарком (`s3.intro.sparknote`, борд `:469`). */
export const INTRO_SPARKNOTE = { lead: 'the line above draws itself · ', bold: 'one point per card' };

/** Возврат на шаг 2 (`s3.intro.back`, борд `:471`). Единственная кнопка деки, нацеленная туда. */
export const INTRO_BACK = '← back to stages';

/**
 * Строка наблюдателя (`s3.intro.observer`, борд `:460`).
 *
 * Счётчик внутри неё — ТОТ ЖЕ `stepCounter`, что печатает шапка оболочки, а не второй литерал
 * `03 / 07`. Борд повторяет число дважды потому, что у него это один `head()`; у нас два узла в
 * разных полосах, и два литерала разъехались бы при первой же правке `STEP_COUNT`.
 */
export const introObserver = (address = 's3') => ({
  lead: '— lifemap · ',
  head: INTRO_HEAD,
  mid: ' · ',
  counter: stepCounter(address),
  trail: ' —',
});

/**
 * Тело интро (`s3.intro.body`, борд `:465`) — единственная длинная строка вида.
 *
 * `{N} cards` внутри янтарное, поэтому строка разложена так же, как лид.
 */
export const introBody = (n) => ({
  lead: 'One card per stage — ',
  accent: `${n} cards`,
  trail: '. Each card is three short steps: seven sliders, four quick choices, three questions in your own words. Answer from memory; the first instinct is the true one.',
});

/**
 * Форвард-CTA интро (`s3.intro.cta`, борд `:470`): `open card I · {имя первого этапа}`.
 *
 * ⚠️ «ПЕРВЫЙ ЭТАП» — САМЫЙ СТАРЫЙ, А НЕ ПЕРВЫЙ РЯД КОНВЕРТА. Шаг 2 держит список новым сверху
 * (`s489.d1`), дека идёт oldest-first (§13 спеки), и римская `I` на интро стоит у САМОГО РАННЕГО
 * этапа. Разворот делает вызывающий (`introStages` ниже) — здесь только строка.
 */
export const introCta = (firstStageName) => `open card I · ${firstStageName}`;

/**
 * Список этапов для интро — oldest-first, с римской и длительностью.
 *
 * ⭐ РАЗВОРОТ ЖИВЁТ ЗДЕСЬ, И ЭТО ТА ЖЕ АСИММЕТРИЯ, ЧТО У КОНТРАКТА. Ряды конверта идут новым
 * сверху, дека — старым сначала; пересчёт `n−1−i` уже живёт в `submitCheck.js` и `toWizardInput.js`
 * (дом — `docs/design/submit-seam.md`), и третьей копии правила здесь не заводится: эта функция
 * не пересчитывает адрес, она только читает ряды в обратном порядке. Если перепутать, римская `I`
 * встанет у САМОГО ПОЗДНЕГО этапа — незаметно в середине списка и максимально неверно на краях.
 *
 * @param {Array} rows ряды конверта (`session.input.stages`), новым сверху
 * @param {number} nowY «сейчас» из конверта — длительность последнего этапа считается до него
 * @returns {Array<{roman: string, name: string, duration: string}>} oldest-first
 */
export function introStages(rows, nowY) {
  const n = rows.length;
  return rows.map((_, i) => {
    const at = n - 1 - i;
    const row = stageRow(rows, at, nowY);
    return { roman: roman(i), name: row.name, duration: row.duration || '' };
  });
}
